import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Clock, Brain, ChevronLeft, ChevronRight, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const LIKERT_OPTIONS = [
  { value: 1, label: 'Strongly Disagree', color: 'bg-red-500' },
  { value: 2, label: 'Disagree', color: 'bg-orange-400' },
  { value: 3, label: 'Neutral', color: 'bg-yellow-400' },
  { value: 4, label: 'Agree', color: 'bg-lime-500' },
  { value: 5, label: 'Strongly Agree', color: 'bg-green-500' },
];

function TakePsychometricTest() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [attempt, setAttempt] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(60 * 60); // 60 minutes
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    startTest();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [testId]);

  const startTest = async () => {
    try {
      // Fetch questions
      const questionsRes = await api.get('/psychometric/questions');
      setQuestions(questionsRes.data);
      
      // Start attempt
      const attemptRes = await api.post(`/psychometric/start?test_id=${testId}`);
      setAttempt(attemptRes.data);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            handleSubmit(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      setLoading(false);
    } catch (error) {
      console.error('Error starting psychometric test:', error);
      toast.error(error.response?.data?.detail || 'Failed to start psychometric test');
      navigate('/dashboard');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrev = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmit = async (autoSubmit = false) => {
    if (submitting) return;
    
    const answeredCount = Object.keys(answers).length;
    if (answeredCount < questions.length && !autoSubmit) {
      toast.warning(`You have answered ${answeredCount} of ${questions.length} questions. Are you sure you want to submit?`);
    }
    
    setSubmitting(true);
    if (timerRef.current) clearInterval(timerRef.current);
    
    try {
      const submission = {
        attempt_id: attempt.id,
        answers: Object.entries(answers).map(([questionId, response]) => ({
          question_id: questionId,
          response: response
        }))
      };
      
      const result = await api.post('/psychometric/submit', submission);
      
      toast.success('Psychometric test submitted successfully!');
      
      // Navigate back to dashboard or results page
      navigate(`/psychometric-results/${attempt.id}`, { 
        state: { result: result.data } 
      });
    } catch (error) {
      console.error('Error submitting test:', error);
      toast.error('Failed to submit test');
      setSubmitting(false);
    }
  };

  const getAnsweredCount = () => Object.keys(answers).length;
  const progress = questions.length > 0 ? (getAnsweredCount() / questions.length) * 100 : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <Brain className="w-16 h-16 text-purple-600 mx-auto animate-pulse" />
          <p className="mt-4 text-lg text-gray-600">Loading Psychometric Assessment...</p>
        </div>
      </div>
    );
  }

  const currentQ = questions[currentQuestion];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Brain className="w-8 h-8 text-purple-600" />
              <div>
                <h1 className="text-lg font-semibold text-gray-900">Psychometric Assessment</h1>
                <p className="text-sm text-gray-500">Question {currentQuestion + 1} of {questions.length}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
                timeRemaining < 300 ? 'bg-red-100 text-red-700' : 'bg-purple-100 text-purple-700'
              }`}>
                <Clock className="w-4 h-4" />
                <span className="font-mono font-medium">{formatTime(timeRemaining)}</span>
              </div>
              
              <Button 
                onClick={() => setShowSubmitDialog(true)}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="submit-psychometric-btn"
              >
                Submit Test
              </Button>
            </div>
          </div>
          
          {/* Progress bar */}
          <div className="mt-3">
            <div className="flex justify-between text-sm text-gray-500 mb-1">
              <span>{getAnsweredCount()} answered</span>
              <span>{questions.length - getAnsweredCount()} remaining</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {currentQ && (
          <Card className="p-8 shadow-lg" data-testid="question-card">
            {/* Question Number Badge */}
            <div className="flex items-center gap-2 mb-4">
              <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                Q{currentQ.question_number}
              </span>
              <span className="text-sm text-gray-500">{currentQ.trait}</span>
            </div>
            
            {/* Question Text */}
            <h2 className="text-xl font-medium text-gray-900 mb-8" data-testid="question-text">
              {currentQ.question_text}
            </h2>
            
            {/* Likert Scale Options */}
            <div className="space-y-3">
              {LIKERT_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleAnswer(currentQ.id, option.value)}
                  data-testid={`likert-option-${option.value}`}
                  className={`w-full p-4 rounded-lg border-2 transition-all duration-200 flex items-center justify-between ${
                    answers[currentQ.id] === option.value
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50/50'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-8 h-8 rounded-full ${option.color} flex items-center justify-center text-white font-bold`}>
                      {option.value}
                    </div>
                    <span className={`text-lg ${answers[currentQ.id] === option.value ? 'font-medium text-purple-700' : 'text-gray-700'}`}>
                      {option.label}
                    </span>
                  </div>
                  {answers[currentQ.id] === option.value && (
                    <CheckCircle2 className="w-6 h-6 text-purple-600" />
                  )}
                </button>
              ))}
            </div>
            
            {/* Navigation */}
            <div className="flex justify-between mt-8 pt-6 border-t">
              <Button
                variant="outline"
                onClick={handlePrev}
                disabled={currentQuestion === 0}
                data-testid="prev-question-btn"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Previous
              </Button>
              
              {currentQuestion < questions.length - 1 ? (
                <Button
                  onClick={handleNext}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="next-question-btn"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={() => setShowSubmitDialog(true)}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="finish-test-btn"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Finish Test
                </Button>
              )}
            </div>
          </Card>
        )}

        {/* Question Navigator */}
        <Card className="mt-6 p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Question Navigator</h3>
          <div className="grid grid-cols-10 gap-2">
            {questions.map((q, idx) => (
              <button
                key={q.id}
                onClick={() => setCurrentQuestion(idx)}
                data-testid={`nav-question-${idx + 1}`}
                className={`w-8 h-8 rounded text-sm font-medium transition-colors ${
                  idx === currentQuestion
                    ? 'bg-purple-600 text-white'
                    : answers[q.id]
                    ? 'bg-green-100 text-green-700 border border-green-300'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {idx + 1}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-4 mt-4 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-purple-600"></div>
              <span>Current</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-green-100 border border-green-300"></div>
              <span>Answered</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-gray-100"></div>
              <span>Not Answered</span>
            </div>
          </div>
        </Card>
      </div>

      {/* Submit Confirmation Dialog */}
      <AlertDialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Submit Psychometric Test?</AlertDialogTitle>
            <AlertDialogDescription>
              You have answered {getAnsweredCount()} of {questions.length} questions.
              {getAnsweredCount() < questions.length && (
                <span className="block mt-2 text-orange-600">
                  Warning: {questions.length - getAnsweredCount()} questions are unanswered.
                </span>
              )}
              <span className="block mt-2">
                Once submitted, you cannot change your answers.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-submit">Continue Test</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleSubmit(false)}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="confirm-submit"
            >
              Submit Test
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default TakePsychometricTest;
